import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@redux/hooks";
import { MaintenanceWindow } from "@redux/slices/maintenanceSlice";
import { getProductConfigByRoute } from "@shared/navigation/SideMenu/productConfig";
import {
  isTelemetryRoute,
  isExemptFromBlock,
} from "kontrolEdge/helpers/maintenanceScope";

/**
 * The maintenance window covering the product the user is currently looking
 * at, or null.
 *
 * Route → product uses the same resolver the side menu uses, so the banner
 * and the menu can never disagree about which product a page belongs to. The
 * store is keyed by the API product name ("Kontrol Edge"), which is what the
 * backend returns.
 */
export function useMaintenanceForCurrentProduct(): MaintenanceWindow | null {
  const location = useLocation();
  const byProduct = useAppSelector((state) => state.maintenance.byProduct);

  return useMemo(() => {
    const config = getProductConfigByRoute(location.pathname);
    if (!config) return null;
    return byProduct[config.apiName] ?? null;
  }, [location.pathname, byProduct]);
}

/**
 * Whether a given product is under maintenance, regardless of the route.
 * Views use this to decide between "no data" and a real status — the Kontrol
 * Edge overview asks for "Kontrol Edge" no matter where it is mounted.
 */
export function useIsProductInMaintenance(apiName: string): boolean {
  const byProduct = useAppSelector((state) => state.maintenance.byProduct);
  return Boolean(byProduct[apiName]);
}

/**
 * The window itself, for surfaces that need its `since` to label what they
 * cannot show.
 */
export function useMaintenanceWindow(apiName: string): MaintenanceWindow | null {
  const byProduct = useAppSelector((state) => state.maintenance.byProduct);
  return byProduct[apiName] ?? null;
}

/**
 * The window announced for a product but not yet started, or null.
 *
 * Deliberately a separate hook from `useMaintenanceWindow`: nothing may
 * treat an upcoming window as an open one. It exists so a customer is told
 * before the dashboard goes dark — which, in `blocked` mode, is the only
 * moment they can be told at all, since the views that would carry the
 * notice are the ones being replaced.
 */
export function useUpcomingWindow(apiName: string): MaintenanceWindow | null {
  const upcoming = useAppSelector(
    (state) => state.maintenance.upcomingByProduct
  );
  return upcoming?.[apiName] ?? null;
}

/**
 * A blocking window in effect for this viewer, regardless of where they are.
 *
 * Route-independent on purpose: chrome outside the routed content — the side
 * menu and its badges — has to react to the window too, and it has no route
 * of its own. Asking "is this route blocked?" there would answer "no"
 * whenever the user happened to be standing somewhere else.
 */
export function useBlockingWindow(): MaintenanceWindow | null {
  const tenantType = useAppSelector((state) => state.userProfile.TenantType);
  const window = useMaintenanceWindow("Kontrol Edge");

  return useMemo(() => {
    if (!window || window.mode !== "blocked") return null;
    if (isExemptFromBlock(tenantType)) return null;
    return window;
  }, [window, tenantType]);
}

/**
 * The window that should replace the current view entirely, or null.
 *
 * The blocking window plus one more condition: the route has to be one whose
 * content is telemetry. Everything else in the product reads the database and
 * stays correct, so blocking it would be theatre.
 */
export function useMaintenanceBlock(): MaintenanceWindow | null {
  const location = useLocation();
  const blockingWindow = useBlockingWindow();

  return useMemo(() => {
    if (!blockingWindow) return null;
    if (!isTelemetryRoute(location.pathname)) return null;
    return blockingWindow;
  }, [blockingWindow, location.pathname]);
}

/**
 * Formats the window start for a "no data since HH:MM" label, in the
 * viewer's own timezone.
 */
export function useFormatWindowStart(): (since: string) => string {
  return useCallback((since: string) => {
    const date = new Date(since);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }, []);
}
